import { useEffect, useRef } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import useSWR from 'swr'

import { fetchSubscriptionSettingsResubscribe } from '@library/api-customer-engagement-experience'
import type {
  ResubscriptionSection,
  SubscriptionSettingsAnalytics,
} from '@library/api-customer-engagement-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

import { RESUB_OPTIMISATION_FLAG } from './useSubscriptionSettingsOverview'

export const RESUB_OPTIMISATION_RESUBSCRIBE_SWR_KEY = 'subscription-settings-resubscribe'

type UseSubscriptionSettingsResubscribeResult = {
  sections: ResubscriptionSection[] | null
  isLoading: boolean
}

export function useSubscriptionSettingsResubscribe(
  userId: string,
): UseSubscriptionSettingsResubscribeResult {
  const [decision] = useDecision(RESUB_OPTIMISATION_FLAG)
  const shouldFetch = decision.enabled

  const { data, error } = useSWR(
    shouldFetch ? `${RESUB_OPTIMISATION_RESUBSCRIBE_SWR_KEY}:${userId}` : null,
    fetchSubscriptionSettingsResubscribe,
  )

  const sections = data?.status === 200 && data.data?.sections?.length ? data.data.sections : null
  const isLoading = shouldFetch && data === undefined && !error
  const pageLoad = data?.status === 200 ? data.pageLoad : undefined

  const hasTrackedPageLoad = useRef(false)
  useEffect(() => {
    if (pageLoad && !hasTrackedPageLoad.current) {
      hasTrackedPageLoad.current = true
      queueTrackingEvent<SubscriptionSettingsAnalytics>(pageLoad)
    }
  }, [pageLoad])

  return { sections, isLoading }
}
